import React, { useState, useEffect, useCallback } from 'react';
import { dashboardAPI, investmentAPI } from '../services/api';
import api from '../services/api';
import { DashboardLayout } from '../components/DashboardLayout';
import { useGym } from '../contexts/GymContext';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { Card, CardContent } from '../components/ui/card';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar, Cell, 
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer 
} from 'recharts';
import { 
  TrendingUp, TrendingDown, DollarSign, Target, Percent, 
  Wallet, Receipt, PiggyBank, ArrowUpRight, ArrowDownRight, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../utils/formatters';
import { PainelExecutivo, PainelDespesas, PainelFaturamento } from './DashboardPaineis';

// Cores padrão do sistema
const PRIMARY_COLOR = '#22C55E';
const SECONDARY_COLOR = '#10B981';
const DANGER_COLOR = '#EF4444';
const WARNING_COLOR = '#F59E0B';

const MONTH_NAMES = ['', 'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
                     'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

// Mini Sparkline component
const Sparkline = ({ data, color = PRIMARY_COLOR, height = 40 }) => {
  if (!data || data.length === 0) return null;
  const chartData = data.map((value, index) => ({ value, index }));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={chartData}>
        <Line type="linear" dataKey="value" stroke={color} strokeWidth={2} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
};

// Card de métrica principal - mesmo estilo da Saúde Financeira
const MetricCard = ({ title, value, variacao, historico, icon: Icon, gradient = false, isNegative = false }) => {
  const isPositive = variacao >= 0;
  const displayColor = isNegative ? DANGER_COLOR : PRIMARY_COLOR;
  
  return (
    <Card className={`border-zinc-800 ${gradient ? 'bg-gradient-to-br from-emerald-900/40 to-zinc-900' : 'bg-zinc-900'}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            {Icon && (
              <div className="p-2 rounded-lg bg-zinc-800/50">
                <Icon className="w-4 h-4" style={{ color: displayColor }} />
              </div>
            )}
            <span className="text-xs font-medium text-zinc-400 uppercase tracking-wider">{title}</span>
          </div>
        </div>
        
        <div className="flex items-end justify-between">
          <div>
            <p className="text-2xl font-bold text-white font-numbers">
              {typeof value === 'number' ? formatCurrency(value) : value}
            </p>
            {variacao !== undefined && variacao !== null && (
              <div className={`flex items-center gap-1 mt-1 text-xs ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                {isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                <span>{Math.abs(variacao)}% vs mês anterior</span>
              </div>
            )}
          </div>
          
          {historico && historico.length > 0 && (
            <div className="w-20 h-10">
              <Sparkline data={historico} color={isNegative ? DANGER_COLOR : PRIMARY_COLOR} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Gauge circular para ROI
const CircularGauge = ({ value, max = 100, size = 80, strokeWidth = 6, color = PRIMARY_COLOR }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const percent = Math.min(Math.abs(value) / max, 1);
  const offset = circumference - percent * circumference;
  
  return (
    <div className="relative">
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#27272a" strokeWidth={strokeWidth} />
        <circle 
          cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={strokeWidth}
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-lg font-bold text-white">{value.toFixed(1)}%</span>
      </div>
    </div>
  );
};

export const DashboardPage = () => {
  const { selectedGym } = useGym();
  const { user } = useAuth();
  const { settings } = useSettings();
  const [overview, setOverview] = useState(null);
  const [monthlyTrend, setMonthlyTrend] = useState([]);
  const [roiData, setRoiData] = useState(null);
  const [monthlyGoals, setMonthlyGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Seleção de painel e período
  const [painelAtivo, setPainelAtivo] = useState('resumo'); // resumo | executivo | despesas | faturamento
  const [modoVisao, setModoVisao] = useState(() => localStorage.getItem('dashboard_modo_visao') || 'operacional'); // operacional | geral
  const [periodoModo, setPeriodoModo] = useState('mes'); // mes | personalizado
  const [mesInicio, setMesInicio] = useState(new Date().getMonth() + 1);
  const [anoInicio, setAnoInicio] = useState(new Date().getFullYear());
  const [mesFim, setMesFim] = useState(new Date().getMonth() + 1);
  const [anoFim, setAnoFim] = useState(new Date().getFullYear());
  const [periodoData, setPeriodoData] = useState(null);
  const [periodoLoading, setPeriodoLoading] = useState(false);

  const PAINEIS = [
    { id: 'resumo', label: 'Resumo do Mês' },
    { id: 'executivo', label: 'Executivo' },
    { id: 'despesas', label: 'Despesas' },
    { id: 'faturamento', label: 'Faturamento' },
  ];
  
  // Configurações do dashboard
  const { cardsToShow, showWelcomeMessage, dashboardLayout, chartAnimations, chartStyle } = settings;

  const months = [
    { value: 1, label: 'Janeiro' }, { value: 2, label: 'Fevereiro' }, { value: 3, label: 'Março' },
    { value: 4, label: 'Abril' }, { value: 5, label: 'Maio' }, { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' }, { value: 8, label: 'Agosto' }, { value: 9, label: 'Setembro' },
    { value: 10, label: 'Outubro' }, { value: 11, label: 'Novembro' }, { value: 12, label: 'Dezembro' }
  ];

  const loadData = useCallback(async () => {
    if (!selectedGym) return;
    
    try {
      setLoading(true);
      const monthName = MONTH_NAMES[selectedMonth];
      const [overviewRes, trendRes, roiRes, goalsRes] = await Promise.all([
        dashboardAPI.getOverview(selectedGym.id, monthName, selectedYear, modoVisao),
        dashboardAPI.getMonthlyTrend(selectedGym.id, 6, modoVisao),
        investmentAPI.getMonthlyROI(selectedGym.id, selectedYear),
        api.get(`/goals?gym_id=${selectedGym.id}`)
      ]);
      setOverview(overviewRes.data);
      setMonthlyTrend(trendRes.data);
      setRoiData(roiRes.data);
      
      const filteredGoals = (goalsRes.data || []).filter(goal => 
        goal.month?.toLowerCase() === monthName.toLowerCase() && goal.year === selectedYear
      );
      setMonthlyGoals(filteredGoals);
    } catch (error) {
      toast.error('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  }, [selectedGym, selectedMonth, selectedYear, modoVisao]);

  useEffect(() => {
    if (selectedGym) {
      loadData();
    }
  }, [selectedGym, loadData]);

  const loadPeriodo = useCallback(async () => {
    if (!selectedGym || painelAtivo === 'resumo') return;
    const [mi, ai, mf, af] = periodoModo === 'mes'
      ? [selectedMonth, selectedYear, selectedMonth, selectedYear]
      : [mesInicio, anoInicio, mesFim, anoFim];
    try {
      setPeriodoLoading(true);
      const res = await dashboardAPI.getPeriodo(selectedGym.id, mi, ai, mf, af, modoVisao);
      setPeriodoData(res.data);
    } catch (error) {
      toast.error('Erro ao carregar dados do período');
    } finally {
      setPeriodoLoading(false);
    }
  }, [selectedGym, painelAtivo, periodoModo, selectedMonth, selectedYear, mesInicio, anoInicio, mesFim, anoFim, modoVisao]);

  useEffect(() => {
    loadPeriodo();
  }, [loadPeriodo]);

  const handleModoChange = (m) => {
    setModoVisao(m);
    localStorage.setItem('dashboard_modo_visao', m);
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bom dia';
    if (hour < 18) return 'Boa tarde';
    return 'Boa noite';
  };

  const getUserName = () => {
    if (user?.name) return user.name.split(' ')[0];
    if (user?.email) return user.email.split('@')[0];
    return 'Usuário';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  // Calcular variações
  const lucro = (overview?.total_revenue || 0) - (overview?.total_expenses || 0);
  const margem = overview?.total_revenue > 0 ? (lucro / overview.total_revenue * 100) : 0;
  const currentROI = roiData?.monthly_roi?.[selectedMonth - 1]?.roi || 0;

  // Preparar dados do gráfico de tendência
  const trendChartData = monthlyTrend.map(item => ({
    name: item.month?.substring(0, 3) || '',
    receita: item.revenue || 0,
    despesa: item.expenses || 0,
    lucro: (item.revenue || 0) - (item.expenses || 0)
  }));

  // Dados para gráfico de pizza
  const expenseChartData = overview?.expenses_by_category
    ? Object.entries(overview.expenses_by_category).map(([name, value]) => ({ name, value }))
    : [];

  // Classes de layout baseadas na configuração
  const getGridClass = () => {
    switch (dashboardLayout) {
      case 'compact':
        return 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4';
      case 'expanded':
        return 'grid-cols-1 md:grid-cols-2';
      default:
        return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4';
    }
  };

  const getCardSize = () => {
    switch (dashboardLayout) {
      case 'compact':
        return 'p-3';
      case 'expanded':
        return 'p-6';
      default:
        return 'p-5';
    }
  };

  // Estilo do gráfico baseado na configuração
  const getChartOpacity = () => chartStyle === 'minimal' ? 0.1 : 0.3;
  const getChartStrokeWidth = () => chartStyle === 'minimal' ? 1 : 2;

  const anos = [2024, 2025, 2026, 2027];
  const selectClass = "h-10 px-3 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-emerald-500 focus:outline-none";
  const renderMesSelect = (value, onChange, testid) => (
    <select value={value} onChange={(e) => onChange(parseInt(e.target.value))} className={selectClass} data-testid={testid}>
      {months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
    </select>
  );
  const renderAnoSelect = (value, onChange, testid) => (
    <select value={value} onChange={(e) => onChange(parseInt(e.target.value))} className={selectClass} data-testid={testid}>
      {anos.map((a) => <option key={a} value={a}>{a}</option>)}
    </select>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6 overflow-x-hidden" data-testid="dashboard-page">
        {/* Header com saudação e filtros */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-2">
          {showWelcomeMessage && (
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white">
                {getGreeting()}, {getUserName()}!
              </h1>
              <p className="text-zinc-400 mt-2 text-base">
                Visão geral do desempenho financeiro
              </p>
            </div>
          )}
          {!showWelcomeMessage && (
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">
                Dashboard
              </h1>
            </div>
          )}
          
          {/* Filtros de período */}
          <div className="flex items-center gap-3 flex-wrap">
            {/* Toggle Operacional / Geral */}
            <div className="flex items-center rounded-lg bg-zinc-800 border border-zinc-700 p-0.5" data-testid="modo-visao-toggle" title="Operacional = só a operação (igual DRE). Geral = tudo que entrou e saiu do banco (visão do presidente): lucro líquido e margem líquida.">
              <button
                onClick={() => handleModoChange('operacional')}
                className={`px-3 h-9 rounded-md text-sm transition-colors ${modoVisao === 'operacional' ? 'bg-emerald-600 text-white' : 'text-zinc-400 hover:text-white'}`}
                data-testid="modo-visao-operacional"
              >
                Operacional
              </button>
              <button
                onClick={() => handleModoChange('geral')}
                className={`px-3 h-9 rounded-md text-sm transition-colors ${modoVisao === 'geral' ? 'bg-emerald-600 text-white' : 'text-zinc-400 hover:text-white'}`}
                data-testid="modo-visao-geral"
              >
                Geral
              </button>
            </div>

            {painelAtivo !== 'resumo' && (
              <div className="flex items-center rounded-lg bg-zinc-800 border border-zinc-700 p-0.5" data-testid="periodo-modo-toggle">
                <button
                  onClick={() => setPeriodoModo('mes')}
                  className={`px-3 h-9 rounded-md text-sm transition-colors ${periodoModo === 'mes' ? 'bg-emerald-600 text-white' : 'text-zinc-400 hover:text-white'}`}
                  data-testid="periodo-modo-mes"
                >
                  Mês
                </button>
                <button
                  onClick={() => setPeriodoModo('personalizado')}
                  className={`px-3 h-9 rounded-md text-sm transition-colors ${periodoModo === 'personalizado' ? 'bg-emerald-600 text-white' : 'text-zinc-400 hover:text-white'}`}
                  data-testid="periodo-modo-personalizado"
                >
                  Período
                </button>
              </div>
            )}

            {(painelAtivo === 'resumo' || periodoModo === 'mes') ? (
              <>
                {renderMesSelect(selectedMonth, setSelectedMonth, 'month-selector')}
                {renderAnoSelect(selectedYear, setSelectedYear, 'year-selector')}
              </>
            ) : (
              <div className="flex items-center gap-2 flex-wrap" data-testid="periodo-personalizado-controls">
                {renderMesSelect(mesInicio, setMesInicio, 'mes-inicio-selector')}
                {renderAnoSelect(anoInicio, setAnoInicio, 'ano-inicio-selector')}
                <span className="text-zinc-500 text-sm px-1">até</span>
                {renderMesSelect(mesFim, setMesFim, 'mes-fim-selector')}
                {renderAnoSelect(anoFim, setAnoFim, 'ano-fim-selector')}
              </div>
            )}
          </div>
        </div>

        {/* Seletor de painéis */}
        <div className="flex items-center gap-2 flex-wrap border-b border-zinc-800 pb-3" data-testid="painel-selector">
          {PAINEIS.map((p) => (
            <button
              key={p.id}
              onClick={() => setPainelAtivo(p.id)}
              className={`px-4 h-9 rounded-lg text-sm font-medium transition-colors ${
                painelAtivo === p.id
                  ? 'bg-emerald-600 text-white'
                  : 'bg-zinc-800/60 text-zinc-400 hover:text-white hover:bg-zinc-800'
              }`}
              data-testid={`painel-tab-${p.id}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Cards principais - layout configurável */}
        {painelAtivo === 'resumo' && (
        <>
        <div className={`grid ${getGridClass()} gap-4`} data-testid="dashboard-summary">
          {cardsToShow.revenue && (
            <MetricCard
              title={modoVisao === 'geral' ? 'Recebido' : 'Faturamento'}
              value={overview?.total_revenue || 0}
              icon={TrendingUp}
              gradient
            />
          )}
          {cardsToShow.expenses && (
            <MetricCard
              title={modoVisao === 'geral' ? 'Saídas (Total)' : 'Despesas'}
              value={overview?.total_expenses || 0}
              icon={TrendingDown}
              isNegative
            />
          )}
          {cardsToShow.profit && (
            <MetricCard
              title={modoVisao === 'geral' ? 'Lucro Líquido' : 'Lucro'}
              value={lucro}
              icon={Wallet}
              gradient
            />
          )}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className={getCardSize()}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="p-2 rounded-lg bg-zinc-800/50">
                      <Percent className="w-4 h-4 text-emerald-400" />
                    </div>
                    <span className="text-xs font-medium text-zinc-400 uppercase tracking-wider">ROI</span>
                  </div>
                  <p className="text-3xl font-bold text-white">{currentROI.toFixed(1)}%</p>
                  <p className="text-sm text-zinc-500 mt-1">{modoVisao === 'geral' ? 'Margem Líquida' : 'Margem'}: {margem.toFixed(1)}%</p>
                </div>
                <CircularGauge 
                  value={currentROI} 
                  max={50} 
                  color={currentROI >= 0 ? PRIMARY_COLOR : DANGER_COLOR}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Área central: Gráfico de Tendência + Metas */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Gráfico de Tendência - 2/3 da largura */}
          {cardsToShow.monthlyTrend && (
            <Card className="lg:col-span-2 bg-zinc-900 border-zinc-800">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-base font-medium text-white">Tendência Mensal</h3>
                  <div className="flex items-center gap-4 text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-0.5 bg-emerald-500 rounded"></div>
                      <span className="text-zinc-400">Receita</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-0.5 bg-red-500 rounded"></div>
                      <span className="text-zinc-400">Despesa</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-0.5 bg-blue-500 rounded"></div>
                      <span className="text-zinc-400">Lucro</span>
                    </div>
                  </div>
                </div>
                
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trendChartData}>
                      <defs>
                        <linearGradient id="colorReceita" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={PRIMARY_COLOR} stopOpacity={getChartOpacity()}/>
                          <stop offset="95%" stopColor={PRIMARY_COLOR} stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorDespesa" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={DANGER_COLOR} stopOpacity={getChartOpacity()}/>
                          <stop offset="95%" stopColor={DANGER_COLOR} stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#71717a', fontSize: 11 }} />
                      <YAxis 
                        axisLine={false} tickLine={false} tick={{ fill: '#71717a', fontSize: 11 }}
                        tickFormatter={(value) => {
                          if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                          if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
                          return value;
                        }}
                      />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px' }}
                        labelStyle={{ color: '#a1a1aa' }}
                        formatter={(value) => [formatCurrency(value)]}
                      />
                      <Area 
                        type="linear" 
                        dataKey="receita" 
                        stroke={PRIMARY_COLOR} 
                        strokeWidth={getChartStrokeWidth()} 
                        fill={chartStyle === 'minimal' ? 'none' : 'url(#colorReceita)'}
                        isAnimationActive={chartAnimations}
                      />
                      <Area 
                        type="linear" 
                        dataKey="despesa" 
                        stroke={DANGER_COLOR} 
                        strokeWidth={getChartStrokeWidth()} 
                        fill={chartStyle === 'minimal' ? 'none' : 'url(#colorDespesa)'}
                        isAnimationActive={chartAnimations}
                      />
                      <Line 
                        type="linear" 
                        dataKey="lucro" 
                        stroke="#3B82F6" 
                        strokeWidth={getChartStrokeWidth()} 
                        dot={false}
                        isAnimationActive={chartAnimations}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Card de Metas - 1/3 da largura */}
          {cardsToShow.goals && (
            <Card className={`bg-zinc-900 border-zinc-800 ${!cardsToShow.monthlyTrend ? 'lg:col-span-3' : ''}`}>
              <CardContent className="p-6">
                <h3 className="text-base font-medium text-white mb-6">Metas do Mês</h3>
                
                {monthlyGoals.length > 0 ? (
                  <div className="space-y-4">
                    {monthlyGoals.slice(0, 4).map((goal, index) => {
                      const progress = goal.target > 0 ? Math.min((goal.current / goal.target) * 100, 100) : 0;
                      const isAchieved = progress >= 100;
                      return (
                        <div key={index} className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-zinc-300">{goal.name}</span>
                            <span className={isAchieved ? 'text-emerald-400' : 'text-zinc-400'}>
                              {progress.toFixed(0)}%
                            </span>
                          </div>
                          <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${chartAnimations ? 'transition-all duration-500' : ''}`}
                              style={{ 
                                width: `${progress}%`,
                                backgroundColor: isAchieved ? PRIMARY_COLOR : WARNING_COLOR
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-40 text-center">
                    <Target className="w-12 h-12 text-zinc-700 mb-3" />
                    <p className="text-zinc-500 text-sm">Nenhuma meta para este mês</p>
                    <p className="text-zinc-600 text-xs mt-1">Adicione metas na aba Metas</p>
                  </div>
                )}
                
                <button className="w-full mt-4 py-2 text-sm text-emerald-400 hover:text-emerald-300 flex items-center justify-center gap-1 transition-colors">
                  Ver todas as metas
                  <ChevronRight className="w-4 h-4" />
                </button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Gráfico de Despesas por Categoria */}
        {painelAtivo === 'resumo' && cardsToShow.expensesByCategory && expenseChartData.length > 0 && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6">
              <h3 className="text-base font-medium text-white mb-6">Despesas por Categoria</h3>
              <div style={{ height: Math.max(expenseChartData.length * 44 + 20, 160) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={expenseChartData} layout="vertical" margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" horizontal={false} />
                    <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: '#71717a', fontSize: 11 }}
                      tickFormatter={(v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                    <YAxis type="category" dataKey="name" width={130} axisLine={false} tickLine={false} tick={{ fill: '#a1a1aa', fontSize: 12 }} />
                    <Tooltip cursor={{ fill: '#27272a55' }} contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px' }} formatter={(value) => [formatCurrency(value)]} />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]} isAnimationActive={chartAnimations}>
                      {expenseChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={DANGER_COLOR} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
        </>
        )}

        {/* Painéis por período (Executivo, Despesas, Faturamento) */}
        {painelAtivo !== 'resumo' && (
          periodoLoading ? (
            <div className="flex items-center justify-center h-64" data-testid="painel-loading">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
            </div>
          ) : (
            <>
              {painelAtivo === 'executivo' && <PainelExecutivo data={periodoData} animate={chartAnimations} />}
              {painelAtivo === 'despesas' && <PainelDespesas data={periodoData} animate={chartAnimations} />}
              {painelAtivo === 'faturamento' && <PainelFaturamento data={periodoData} animate={chartAnimations} />}
            </>
          )
        )}
      </div>
    </DashboardLayout>
  );
};

export default DashboardPage;
